import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import {
  Bot, Search, UserCheck, PenTool, Heart,
  Play, Pause, Activity, Clock, Zap,
  MessageSquare, Mail, TrendingUp,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import type { FunnelAgent } from "@/hooks/useSalesFunnel";

const AGENT_ICONS: Record<string, typeof Bot> = {
  prospecteur: Search,
  qualificateur: UserCheck,
  redacteur: PenTool,
  nurturing: Heart,
};

const AGENT_COLORS: Record<string, string> = {
  prospecteur: "hsl(var(--primary))",
  qualificateur: "hsl(200, 80%, 55%)",
  redacteur: "hsl(150, 70%, 45%)",
  nurturing: "hsl(340, 75%, 55%)",
};

interface Props {
  agents: FunnelAgent[];
  loading: boolean;
  onRunAgent?: (agentId: string) => void;
}

export function AgentsTab({ agents, loading, onRunAgent }: Props) {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(agents[0]?.agent_id || null);

  const totalMissions = agents.reduce((s, a) => s + a.completed_missions, 0);
  const activeCount = agents.filter((a) => a.active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Agents actifs", value: `${activeCount}/${agents.length}`, icon: Bot, color: "text-primary" },
          { label: "Missions NanoCorp", value: totalMissions, icon: Zap, color: "text-amber-400" },
          { label: "Agents connectés", value: agents.length, icon: TrendingUp, color: "text-emerald-400" },
          { label: "Dernier scan", value: agents[0]?.last_run ? formatDistanceToNow(new Date(agents[0].last_run), { addSuffix: true, locale: fr }) : "—", icon: Clock, color: "text-blue-400" },
        ].map((stat) => (
          <Card key={stat.label} className="p-4 bg-card/50 backdrop-blur border-border/30" data-testid={`funnel-stat-${stat.label.replace(/\s/g, '-').toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-muted/50 ${stat.color}`}>
                <stat.icon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-lg font-bold">{stat.value}</div>
                <div className="text-[10px] text-muted-foreground">{stat.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Agent Cards */}
      <div className="space-y-3">
        {agents.map((agent, i) => {
          const Icon = AGENT_ICONS[agent.agent_id] || Bot;
          const color = AGENT_COLORS[agent.agent_id] || "hsl(var(--primary))";
          const isExpanded = expandedAgent === agent.agent_id;
          return (
            <motion.div
              key={agent.agent_id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <Card
                className={`p-4 bg-card/50 backdrop-blur border-border/30 cursor-pointer transition-all hover:border-primary/20 ${
                  isExpanded ? "border-primary/30 shadow-lg shadow-primary/5" : ""
                }`}
                onClick={() => setExpandedAgent(isExpanded ? null : agent.agent_id)}
                data-testid={`funnel-agent-${agent.agent_id}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2.5 rounded-xl relative"
                      style={{ background: color + "18", border: `1px solid ${color}33` }}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                      {agent.active && (
                        <motion.div
                          className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-card"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                        />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{agent.name}</span>
                        <Badge
                          variant="outline"
                          className={`text-[9px] px-1.5 py-0 ${
                            agent.active ? "border-emerald-500/30 text-emerald-400" : "border-muted-foreground/20 text-muted-foreground"
                          }`}
                        >
                          {agent.active ? "Actif" : "En pause"}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground">{agent.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-4 text-center">
                      <div>
                        <div className="text-sm font-bold">{agent.completed_missions}</div>
                        <div className="text-[9px] text-muted-foreground">Missions</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 mt-4 border-t border-border/30">
                      {/* Latest Briefing */}
                      {agent.latest_briefing && (
                        <div className="mb-4 p-3 rounded-lg bg-muted/30 border border-border/30">
                          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            Dernier rapport
                          </h4>
                          <p className="text-xs text-foreground whitespace-pre-line">{agent.latest_briefing}</p>
                          {agent.last_run && (
                            <p className="text-[9px] text-muted-foreground mt-2">
                              {formatDistanceToNow(new Date(agent.last_run), { addSuffix: true, locale: fr })}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Recent Logs */}
                      {agent.recent_logs.length > 0 && (
                        <>
                          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            Activité récente
                          </h4>
                          <div className="space-y-2">
                            {agent.recent_logs.map((log, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                                <Activity className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[11px] font-medium truncate">{log.action}</div>
                                </div>
                                <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                                  {formatDistanceToNow(new Date(log.created_at), { addSuffix: true, locale: fr })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {!agent.latest_briefing && agent.recent_logs.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-4">
                          Aucune activité. Lancez cet agent depuis NanoCorp.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
